import { TimeRange } from '@mi-front/core';
import { useStore } from 'zustand';
import type { AudioPlaybackStore } from '../audio/useAudioPlaybackStore';
import { PlaybackStoreApi } from '../context/playback.context';
import { useOptionalPlaybackStoreApiContext } from '../context/useOptionalPlaybackStoreApiContext';
import { usePlaybackStoreApiContext } from '../context/usePlaybackStoreApiContext';
import type { VideoPlaybackStore } from '../video/useVideoPlaybackStore';

type PlaybackStore = AudioPlaybackStore | VideoPlaybackStore;

export function usePlaybackStore(): PlaybackStoreApi {
  return usePlaybackStoreApiContext();
}

/**
 * Returns true if a PlaybackProvider exists in the component tree.
 * Useful for conditionally using parent playback context vs creating a new one.
 */
export function useHasPlaybackContext(): boolean {
  return useOptionalPlaybackStoreApiContext() !== undefined;
}

/**
 * Hook to select state from the playback store.
 * Works with both audio and video stores (union type).
 *
 * ⚠️ WARNING: If your selector returns a new object (e.g., `(s) => ({ a: s.a, b: s.b })`),
 * it will cause infinite re-renders because Zustand uses reference equality.
 *
 * Solutions:
 * 1. Use individual selectors: `const a = usePlaybackState(s => s.a); const b = usePlaybackState(s => s.b);`
 * 2. Use useMemo to combine individual selectors (see PlaybackDebug.tsx for example)
 * 3. Select the entire state object if you need multiple fields: `usePlaybackState(s => s)`
 */
export function usePlaybackState<T>(selector: (s: PlaybackStore) => T): T {
  const store = usePlaybackStore();
  // Type assertion needed because Zustand's useStore expects the selector to match the store type exactly,
  // but we're using a union type (AudioPlaybackStore | VideoPlaybackStore)
  return useStore(store, selector as (s: AudioPlaybackStore | VideoPlaybackStore) => T);
}

export function usePlaybackCurrentTs() {
  return usePlaybackState((s) => s.currentTs);
}

export function usePlaybackIsPlaying() {
  return usePlaybackState((s) => s.isPlaying);
}

export function usePlaybackIsPaused() {
  return usePlaybackState((s) => !s.isPlaying);
}

export function usePlaybackIsSeeking() {
  return usePlaybackState((s) => s.isSeeking);
}

/**
 * Hook to get the pause method from the playback store.
 */
export function usePlaybackPause() {
  return usePlaybackState((s) => s.pause);
}

/**
 * Hook to get the play method from the playback store.
 */
export function usePlaybackPlay() {
  return usePlaybackState((s) => s.play);
}

/**
 * Hook to get the resume method from the playback store.
 */
export function usePlaybackResume() {
  return usePlaybackState((s) => s.play);
}

/**
 * Returns the playback range (from/to timestamps) from media configuration.
 * Returns undefined if no playback range is configured.
 *
 * When called with `usePlaybackRange<true>`, returns a safe non-null TimeRange
 * with populated from and to values (assumes range is always available).
 */
export function usePlaybackRange(): TimeRange<number> | null | undefined;
export function usePlaybackRange<_T extends true>(): TimeRange<number>;
export function usePlaybackRange<_T extends boolean = false>():
  | TimeRange<number>
  | null
  | undefined {
  const range = usePlaybackState((s) => s.playbackRange);

  // Return the range as-is. The type system ensures that when T is true,
  // callers get a non-nullable TimeRange type. Runtime validation should be
  // handled by the caller or by ensuring the range is set before using the safe variant.
  return range as _T extends true ? TimeRange<number> : TimeRange<number> | null | undefined;
}

/**
 * Returns a function to seek to a specific timestamp.
 * Automatically uses the configured stopTs from playback range.
 */
export function usePlaybackSeekInRange(): (ts: number) => void {
  const play = usePlaybackPlay();
  const range = usePlaybackRange();

  return (ts: number) => {
    play({ startTs: ts, stopTs: range?.to });
  };
}
